import { useMemo, useState } from 'react';
import { ForbiddenError } from '../../api/errors';
import { inventoryApi } from '../../api/inventory-api-instance';
import type { Customer, Item, Part, Site } from '../../domain/inventory';
import { statusLabels } from '../../domain/status';
import { downloadCsv } from '../../lib/csv';
import { siteLabel } from '../../lib/site-label';
import { registerCsv, registerCsvFilename } from '../item-register/register-csv';

interface ExportDialogProps {
  items: Item[];
  partsById: Map<string, Part>;
  sitesById: Map<string, Site>;
  customersById: Map<string, Customer>;
  onClose: () => void;
}

const EXPORT_LIMIT = 5000;
// The country whose register is sent each month (tidaltech/issues#9521).
const DEFAULT_REGISTER_COUNTRY = 'Chile';

function csvCell(value: string | number | boolean | undefined | null): string {
  const text = value === undefined || value === null ? '' : String(value);
  return `"${text.replaceAll('"', '""')}"`;
}

export function ExportDialog({
  items,
  partsById,
  sitesById,
  customersById,
  onClose,
}: ExportDialogProps) {
  const exportItems = items.slice(0, EXPORT_LIMIT);
  const countries = useMemo(
    () =>
      [
        ...new Set(
          [...sitesById.values()].map((site) => site.labels?.country?.trim() ?? '').filter(Boolean),
        ),
      ].sort((left, right) => left.localeCompare(right)),
    [sitesById],
  );
  const [registerCountry, setRegisterCountry] = useState('');
  const country =
    registerCountry ||
    (countries.includes(DEFAULT_REGISTER_COUNTRY)
      ? DEFAULT_REGISTER_COUNTRY
      : (countries[0] ?? ''));
  const [registerBusy, setRegisterBusy] = useState(false);
  const [registerError, setRegisterError] = useState('');

  // The register comes from leatherback, which works out each item's
  // condition, so it covers every item in the country whatever the filters.
  async function downloadRegister(): Promise<void> {
    setRegisterBusy(true);
    setRegisterError('');
    try {
      const register = await inventoryApi.listItemRegister(country);
      downloadCsv(registerCsvFilename(country, register), registerCsv(register));
      onClose();
    } catch (error) {
      setRegisterError(
        error instanceof ForbiddenError
          ? 'Only Tidal staff can download a country register.'
          : `The register couldn't be downloaded: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setRegisterBusy(false);
    }
  }

  function download(): void {
    const headings = [
      'item_id',
      'part_id',
      'part_type',
      'build_stage',
      'hardware_revision',
      'customer',
      'assigned_site',
      'current_location_site',
      'location_detail',
      'status',
      'status_label',
      'needs_attention',
      'attention_reason',
      'last_inspection',
      'last_maintenance',
      'last_modified',
      'modified_by',
      'version',
    ];
    const lines = exportItems.map((item) => {
      const part = partsById.get(item.partId);
      const assignedSite = sitesById.get(item.siteId);
      const locationSite = sitesById.get(item.locationSiteId ?? item.siteId);
      const customer = customersById.get(assignedSite?.customerId ?? '');
      return [
        item.id,
        item.partId,
        part?.type,
        part?.build,
        item.hardwareRevision,
        customer?.displayName ?? customer?.name,
        siteLabel(assignedSite, item.siteId),
        siteLabel(locationSite, item.locationSiteId ?? item.siteId),
        item.location,
        item.status ?? 'STATUS_UNSPECIFIED',
        statusLabels[item.status ?? 'STATUS_UNSPECIFIED'],
        Boolean(item.needsAttention),
        item.attentionReason,
        item.inspectedDate,
        item.maintenanceDate,
        item.lastModified,
        item.lastModifiedBy,
        item.version,
      ]
        .map(csvCell)
        .join(',');
    });
    const csv = [headings.map(csvCell).join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `leatherback-inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    onClose();
  }

  return (
    <div className="drawer-overlay centered-overlay" role="presentation" onClick={onClose}>
      <div
        className="confirmation-dialog"
        role="dialog"
        aria-label="Review inventory export"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="drawer-heading">
          <div>
            <h2>Export items</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close export review"
          >
            ×
          </button>
        </div>
        <p>
          <strong>{exportItems.length.toLocaleString()}</strong> filtered records.
        </p>
        <dl className="review-grid">
          <dt>Format</dt>
          <dd>CSV, UTF-8</dd>
          <dt>Maximum</dt>
          <dd>{EXPORT_LIMIT.toLocaleString()} records</dd>
          <dt>Scope</dt>
          <dd>Current filters and sort order</dd>
        </dl>
        <div className="dialog-actions">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="primary"
            onClick={download}
            disabled={exportItems.length === 0}
          >
            Download CSV
          </button>
        </div>
        <section className="export-register" aria-labelledby="export-register-heading">
          <h3 id="export-register-heading">Country register</h3>
          <p>
            Every item at a country's sites, including items in transit, with its condition. This is
            the register sent to HEAV ADM for Chile each month.
          </p>
          <div className="export-register-controls">
            <label>
              Country
              <select
                value={country}
                onChange={(event) => setRegisterCountry(event.target.value)}
                disabled={registerBusy || countries.length === 0}
              >
                {countries.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => void downloadRegister()}
              disabled={registerBusy || !country}
            >
              {registerBusy ? 'Preparing…' : 'Download register'}
            </button>
          </div>
          {registerError ? (
            <p className="form-error" role="alert">
              {registerError}
            </p>
          ) : null}
        </section>
      </div>
    </div>
  );
}
